/**
 * O admin novo, todo sobre o Chassi. Primeira fatia: entrada, acesso, Chassi e Sair.
 *
 * O fluxo de entrada reproduz o do admin atual (`portal/admin.html:3491`): lê a sessão, confere a
 * linha da pessoa em `usuarios_sistema`, e ou entra, ou vê "Essa conta não tem acesso a este
 * painel." A diferença é que tudo é desenhado pelo Chassi, e o Sair do Chassi volta para o login
 * NOVO. As telas do painel ainda não migraram: a área útil diz isso, e não inventa conteúdo.
 */

// Estado e efeito do React: a leitura da sessão e do acesso é assíncrona.
import { useCallback, useEffect, useState } from 'react';
// A tela de login do Chassi e o tipo da sessão que o provedor devolve.
import { TelaLogin, type Sessao } from '@complexity/auth';
// O Chassi: 3 frames, menu, cabeçalho, ajuda e o Sair no painel da conta.
import { Chassi } from '@complexity/chassi';
// O dicionário: a frase em português é a chave (e o único idioma ligado neste projeto).
import { t } from '@complexity/i18n';
// O registro valida o manifesto na partida, e não a cada renderização.
import { registrarProduto } from '@complexity/product-kit';
// O manifesto do admin (nome, marca, menu, textos do login), que mora no catálogo.
import produtoAdmin from '../product.config.js';
// O provedor, o ambiente e a volta do Google.
import { AMBIENTE_DA_TELA, provedor, urlDeRetorno } from './identidade.js';
// A leitura de `usuarios_sistema`, com o mesmo critério do admin atual.
import { PAPEL_LABEL, fonteDeDados, lerAcessoAoPainel, type AcessoAoPainel } from './dados/acesso.js';
// O que cada papel vê e onde entra, igual ao admin atual.
import { permissoesDoPapel, rotaInicial } from './papeis.js';
// A área útil: qual tela desenha em cada rota.
import { AreaUtil } from './rotas.js';
// O contexto que entrega sessão, papel e navegação a toda tela.
import { ProvedorDoPainel } from './contexto.js';

// Na carga do módulo: um manifesto inválido precisa quebrar na partida.
registrarProduto(produtoAdmin);

// Os quatro estados da tela, e nenhum outro.
type Estado =
  | { readonly tipo: 'carregando' }
  | { readonly tipo: 'login' }
  | { readonly tipo: 'negado' }
  | { readonly tipo: 'dentro'; readonly sessao: Sessao; readonly acesso: AcessoAoPainel };

export function App() {
  // Começa carregando: evita piscar o login para quem já está logado.
  const [estado, setEstado] = useState<Estado>({ tipo: 'carregando' });
  // A rota ativa do menu do Chassi. Ganha o valor certo ao entrar, pelo papel.
  const [rota, setRota] = useState('/painel');

  // Com uma sessão na mão, a mesma pergunta do admin atual: esta conta tem acesso ao painel?
  const entrarCom = useCallback(async (sessao: Sessao) => {
    const acesso = await lerAcessoAoPainel(sessao.token, sessao.usuario.email);
    // A área de entrada depende do papel, como no admin atual (`admin.html:3523`).
    if (acesso) setRota(rotaInicial(acesso.papel));
    setEstado(acesso ? { tipo: 'dentro', sessao, acesso } : { tipo: 'negado' });
  }, []);

  useEffect(() => {
    // Protege contra atualizar estado de um componente que já saiu da tela.
    let vivo = true;
    (async () => {
      // O provedor precisa do cliente criado antes de qualquer chamada. Na volta do Google, é aqui
      // que o supabase-js lê a sessão da URL.
      await provedor.inicializar();
      const sessao = await provedor.getSession();
      if (!vivo) return;
      // Sem sessão: o login. Com sessão: confere o acesso.
      if (!sessao) setEstado({ tipo: 'login' });
      else await entrarCom(sessao);
    })().catch(() => {
      // Falha ao ler a sessão (rede fora, por exemplo): mostra o login, que diz o erro ao tentar.
      if (vivo) setEstado({ tipo: 'login' });
    });
    // Ao desmontar, o que ainda estiver pendente não mexe mais no estado.
    return () => {
      vivo = false;
    };
  }, [entrarCom]);

  // O Sair do Chassi: encerra a sessão no Supabase e volta ao login NOVO.
  const sair = useCallback(() => {
    void provedor
      .signOut()
      // Mesmo que o encerramento no servidor falhe, a tela não fica dentro do painel.
      .finally(() => setEstado({ tipo: 'login' }));
  }, []);

  // Enquanto a sessão é lida, uma linha só.
  if (estado.tipo === 'carregando') {
    return (
      <main className="flex min-h-screen items-center justify-center p-cx-6">
        <p className="text-cx-sm text-texto-suave">{t('Carregando...')}</p>
      </main>
    );
  }

  // Sem sessão: o login do Chassi.
  if (estado.tipo === 'login') {
    return (
      <TelaLogin
        produto={produtoAdmin}
        provedor={provedor}
        aoEntrar={(resultado) => void entrarCom(resultado.sessao)}
        urlRetorno={urlDeRetorno()}
        ambiente={AMBIENTE_DA_TELA}
      />
    );
  }

  // Conta sem linha em `usuarios_sistema`: o texto exato do admin atual (`admin.html:1748`).
  if (estado.tipo === 'negado') {
    return (
      <main className="flex min-h-screen items-center justify-center p-cx-6">
        <p className="text-cx-md text-texto">{t('Essa conta não tem acesso a este painel.')}</p>
      </main>
    );
  }

  // Dentro: o Chassi, com os dados de quem entrou no painel da conta, e o Sair lá dentro.
  const { sessao, acesso } = estado;
  return (
    <Chassi
      produto={produtoAdmin}
      sessao={sessao}
      ambiente={AMBIENTE_DA_TELA}
      // Sem o filtro (`?etapa=...`): o Chassi compara a rota do menu com igualdade (`AppShell.tsx:300`).
      rotaAtiva={rota.split('?')[0] ?? rota}
      aoNavegar={setRota}
      aoSair={sair}
      // O menu mostra só as áreas que o papel vê, como `aplicarRestricoesDePapel` faz hoje.
      permissoesDoUsuario={permissoesDoPapel(acesso.papel)}
      dadosDaConta={[
        // `null` vira "Não cadastrado" no Chassi: dado que não existe é dito, nunca inventado.
        { rotulo: 'Nome', valor: sessao.usuario.nome },
        { rotulo: 'E-mail', valor: acesso.email },
        // O rótulo do papel, como o admin atual escreve (`admin.html:3502`).
        { rotulo: 'Papel', valor: PAPEL_LABEL[acesso.papel] ?? acesso.papel },
      ]}
    >
      <ProvedorDoPainel value={{ sessao, acesso, dados: fonteDeDados, rota, navegar: setRota }}>
        <AreaUtil rota={rota} />
      </ProvedorDoPainel>
    </Chassi>
  );
}
